import React from 'react';
import styled, { css } from 'styled-components';

const CardVariants = {
  default: css`
    background-color: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  `,
  elevated: css`
    background-color: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    box-shadow: ${({ theme }) => theme.shadows.md};
  `,
  outlined: css`
    background-color: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    border: 1px solid ${({ theme }) => theme.colors.border};
  `,
  flat: css`
    background-color: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  `
};

const StyledCard = styled.div`
  overflow: hidden;
  transition: ${({ theme }) => theme.transition.default};
  
  ${({ $variant }) => CardVariants[$variant] || CardVariants.default}
  
  ${({ $withHover }) =>
    $withHover &&
    css`
      &:hover {
        transform: translateY(-4px);
        box-shadow: ${({ theme }) => theme.shadows.lg};
      }
    `}
    
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
`;

const CardHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: ${({ $divider, theme }) => ($divider ? `1px solid ${theme.colors.border}` : 'none')};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  font-weight: ${({ theme }) => theme.typography.weights.semiBold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CardContent = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
`;

const CardFooter = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-top: ${({ $divider, theme }) => ($divider ? `1px solid ${theme.colors.border}` : 'none')};
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => 
    $align === 'left' ? 'flex-start' : 
    $align === 'right' ? 'flex-end' : 
    'space-between'
  };
`;

const Card = ({
  children,
  variant = 'default',
  withHover = false,
  fullWidth = false,
  ...props
}) => {
  return (
    <StyledCard
      $variant={variant}
      $withHover={withHover}
      $fullWidth={fullWidth}
      {...props}
    >
      {children}
    </StyledCard>
  );
};

Card.Header = ({ children, divider = false, ...props }) => (
  <CardHeader $divider={divider} {...props}>
    {typeof children === 'string' ? <CardTitle>{children}</CardTitle> : children}
  </CardHeader>
);

Card.Content = ({ children, ...props }) => (
  <CardContent {...props}>{children}</CardContent>
);

Card.Footer = ({ children, divider = true, align = 'between', ...props }) => (
  <CardFooter $divider={divider} $align={align} {...props}>
    {children}
  </CardFooter>
);

export default Card; 